import DOMPurify from 'isomorphic-dompurify';
import { parse } from 'cookie';
import { FeatureURI, CategoryID, AdvertiserType } from '@sbt-web/network/types';
import { LABEL_REQUEST_ID, getRequestId } from '@sbt-web/request-id-handler';
export { default as queueTask } from 'post-task';

/**
 * This function formats a given number adding dots between hundreds.
 */
function addDotsToNumber(num) {
    let formatted = Math.abs(num).toFixed(0);
    for (let i = formatted.length - 3; i > 0; i = i - 3) {
        formatted = [formatted.slice(0, i), '.', formatted.slice(i)].join('');
    }
    return formatted;
}

/**
 * assertIsNodeInstance
 *
 * @private
 */
function assertIsNodeInstance(element) {
    if (element instanceof HTMLElement) {
        return;
    }
    else {
        throw new Error('The element provided is not of HTMLElement instance.');
    }
}

/**
 * This function adds a CSS class to a given HTML element.
 */
function addNodeClass(element, CSSClass) {
    assertIsNodeInstance(element);
    element.classList.add(CSSClass);
}

/**
 * This function can be used inside a `switch` statement to make it exhaustive
 * (TypeScript only).
 */
function assertNever(x) {
    throw new Error(`Unexpected object: ${x}`);
}

/**
 * This function creates a capitalized version of a given string.
 */
function capitalize(str) {
    return str
        .toLowerCase()
        .split(' ')
        .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
        .join(' ');
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const debounce = (func, delay) => {
    let timeout;
    const debounced = (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), delay);
    };
    return debounced;
};

/**
 * Generate a random ID composed by the current timestamp
 * concatenated with a dot and a random string of 10 characters in base36.
 *
 * Example: `1667412119253.at34uersu7`
 */
function generateEventID() {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 12);
    return `${timestamp}.${randomString}`;
}

/**
 * This function triggers a CustomEvent with a given name and payload to the
 * `window` object.
 */
function dispatchExternalEvent(eventName, data) {
    window.dispatchEvent(new CustomEvent(eventName, { detail: data }));
    return false;
}
function dispatchEventForClients(eventName, data, clients) {
    window.dispatchEvent(new CustomEvent(eventName, { detail: data }));
    if (clients?.indexOf('gtm') !== -1) {
        window.dataLayer?.push({
            event: eventName,
            ss_event_id: generateEventID(),
            ...data,
        });
    }
    return false;
}

/**
 * See the [documentation of DOMPurify](https://github.com/cure53/DOMPurify/blob/main/README.md#can-i-configure-dompurify)
 * for more info about the configuration.
 */
const getSafeHTML = (dirtyText, cfg) => DOMPurify.sanitize(dirtyText, cfg);

/**
 * This function determines whether code is running on the browser or not.
 */
function isBrowser() {
    return typeof window !== 'undefined';
}

const getCookie = (name) => {
    const value = `; ${isBrowser() ? document.cookie : ''}`;
    const parts = value.split(`; ${name}=`);
    if (parts == undefined || parts.length < 2) {
        return '';
    }
    return parts[1]?.split(';').shift();
};

const parseCookieValue = (cookie) => {
    const decodedCookie = decodeURIComponent(cookie);
    const id = decodedCookie.split(',')[0];
    return id;
};

var cookieName = '_pulse2data';

const getEnvironmentIdFromCookie = () => {
    try {
        const pulseCookie = getCookie(cookieName);
        if (pulseCookie != undefined) {
            const id = parseCookieValue(pulseCookie);
            return id;
        }
        else {
            console.error('The Pulse cookie could not be read to extract the EnvironmentID.');
            return null;
        }
    }
    catch (e) {
        console.error('Error while parsing the Cookie to get the Environment ID:', e.message);
        return null;
    }
};

const getEnvironmentIdFromCookieHeader = (req) => {
    if (!req || isBrowser()) {
        return null;
    }
    try {
        const cookieHeader = req.headers['cookie'];
        if (cookieHeader == null) {
            console.error('The cookie header could not be read.');
            return null;
        }
        const parsedCookies = parse(cookieHeader);
        const pulseCookie = parsedCookies[cookieName];
        if (pulseCookie != undefined) {
            const id = parseCookieValue(pulseCookie);
            return id;
        }
        else {
            console.error('The Pulse cookie could not be read to extract the Environment ID.');
            return null;
        }
    }
    catch (e) {
        console.error('Error while parsing the Cookie header to get the Environment ID:', e.message);
        return null;
    }
};

/**
 * This function creates a valid escaped RegExp string, to be safely used with
 * a `RegExp()` constructor.
 */
function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

const extractDomainFromHostname = (hostname) => {
    const domainFragments = hostname.split('.');
    const len = domainFragments.length;
    if (len < 2 || domainFragments[0] == '' || domainFragments[1] == '') {
        throw new RangeError(`There is only a top-level domain, the hostname ${hostname} is not well-formatted.`);
    }
    const level2Domain = domainFragments[len - 2], level1Domain = domainFragments[len - 1].split(':')[0];
    return `.${level2Domain}.${level1Domain}`;
};

const extractUtmFromSearchParams = (searchParams) => {
    const result = new URLSearchParams();
    Object.entries(searchParams).forEach(([key, value]) => {
        if (key.startsWith('utm_')) {
            result.append(key, `${value}`);
        }
    });
    return result;
};
const extractUtmFromLocation = () => {
    const result = new URLSearchParams();
    if (isBrowser()) {
        new URLSearchParams(window.location.search).forEach((value, key) => {
            if (key.startsWith('utm_')) {
                result.append(key, `${value}`);
            }
        });
    }
    return result;
};

/**
 * Evaluate a feature of type boolean and returns its boolean value
 */
function evalBoolFeature(feat) {
    if (feat?.values?.[0]) {
        return feat.values[0].key === '1';
    }
    return false;
}

const room = (feat, { short }) => {
    const { key, value } = feat.values[0];
    switch (key) {
        case '11':
            return short ? '+10 Locali' : 'Più di 10 Locali';
        default:
            return `${value} ${value === '1' ? 'Locale' : 'Locali'}`;
    }
};
const mileage = (feat) => `${feat.values[0].value} km`;
const floor = (feat, { short }) => {
    const { key, value } = feat.values[0];
    switch (key) {
        case '1':
            return short ? 'Interr.' : value;
        case '2':
            return short ? 'Semint.' : value;
        case '3':
            return short ? 'Rialz.' : value;
        case '4':
            return short ? 'Piano T' : 'Terra';
        case '12':
            return short ? '+7° Piano' : 'Oltre il 7° Piano';
        default:
            return `${value}° Piano`;
    }
};
const bathrooms = (feat, { short }) => {
    const { key, value } = feat.values[0];
    switch (key) {
        case '4':
            return short ? '+3 Bagni' : 'Più di 3 Bagni';
        default:
            return `${value} ${value === '1' ? 'Bagno' : 'Bagni'}`;
    }
};
const multiLevel = (feat) => evalBoolFeature(feat) ? 'Su più livelli' : '';
const furnished = (feat) => evalBoolFeature(feat) ? 'Arredata' : '';
const roomType = (feat) => {
    const { key, value } = feat.values[0];
    switch (key) {
        case '1':
            return 'Singola';
        case '2':
            return 'Condivisa';
        default:
            return value;
    }
};

const FeatureConfigurations = {
    [FeatureURI.Bathrooms]: { placeholder: 'Bagni', formatter: bathrooms },
    [FeatureURI.CarType]: { placeholder: 'Tipologia' },
    [FeatureURI.Floor]: { placeholder: 'Piano', formatter: floor },
    [FeatureURI.Fuel]: { placeholder: 'Carburante' },
    [FeatureURI.Furnished]: { placeholder: 'Arredata', formatter: furnished },
    [FeatureURI.Gearbox]: { placeholder: 'Cambio' },
    [FeatureURI.Mileage]: { placeholder: 'km', formatter: mileage },
    [FeatureURI.MultiLevel]: { placeholder: 'Piano', formatter: multiLevel },
    [FeatureURI.Pollution]: { placeholder: 'Emissioni' },
    [FeatureURI.RegisterDate]: { placeholder: 'Mese/Anno' },
    [FeatureURI.Room]: { placeholder: 'Locali', formatter: room },
    [FeatureURI.RoomType]: { formatter: roomType },
    [FeatureURI.Size]: { placeholder: 'mq' },
    [FeatureURI.VehicleStatus]: { placeholder: 'Tipologia' },
    [FeatureURI.Year]: { placeholder: 'Anno' },
    [FeatureURI.ItemCondition]: { placeholder: 'Condizione' },
    [FeatureURI.ComputerType]: {
        placeholder: 'Tipologia',
    },
    [FeatureURI.AudiovideoType]: {
        placeholder: 'Tipologia',
    },
    [FeatureURI.PhoneType]: {
        placeholder: 'Tipologia',
    },
    [FeatureURI.ClothingType]: {
        placeholder: 'Tipologia',
    },
    [FeatureURI.ChildrenType]: {
        placeholder: 'Tipologia',
    },
    [FeatureURI.BookType]: {
        placeholder: 'Tipologia',
    },
    [FeatureURI.SportType]: {
        placeholder: 'Tipologia',
    },
    [FeatureURI.BicycleType]: {
        placeholder: 'Tipologia',
    },
    [FeatureURI.HobbyType]: {
        placeholder: 'Tipologia',
    },
};
const MainDataFeatures = {
    // MOTORS
    [CategoryID.Auto]: [
        FeatureURI.VehicleStatus,
        FeatureURI.RegisterDate,
        FeatureURI.Mileage,
        FeatureURI.Fuel,
        FeatureURI.Gearbox,
        FeatureURI.Pollution,
        FeatureURI.CarType,
    ],
    [CategoryID.Moto]: [
        FeatureURI.VehicleStatus,
        FeatureURI.RegisterDate,
        FeatureURI.Mileage,
        FeatureURI.MotorbicycleType,
        FeatureURI.CubicCapacity,
    ],
    // REAL ESTATE
    [CategoryID.Appartamenti]: [
        FeatureURI.Size,
        FeatureURI.Room,
        FeatureURI.Floor,
        FeatureURI.Bathrooms,
    ],
    [CategoryID.Ville]: [
        FeatureURI.Size,
        FeatureURI.Room,
        FeatureURI.MultiLevel,
        FeatureURI.Bathrooms,
    ],
    [CategoryID.UfficiLocali]: [
        FeatureURI.Size,
        FeatureURI.Room,
        FeatureURI.Floor,
        FeatureURI.Bathrooms,
    ],
    [CategoryID.CaseVacanza]: [
        FeatureURI.Size,
        FeatureURI.Room,
        FeatureURI.Floor,
        FeatureURI.Bathrooms,
    ],
    [CategoryID.CamerePostiLetto]: [
        FeatureURI.RoomType,
        FeatureURI.Furnished,
        FeatureURI.Floor,
        FeatureURI.Bathrooms,
    ],
    [CategoryID.TerreniRustici]: [FeatureURI.Size],
    [CategoryID.LoftMansarde]: [FeatureURI.Size, FeatureURI.Room],
    [CategoryID.GarageBox]: [FeatureURI.Size],
    // Casa & Persona
    [CategoryID.AbbigliamentoAccessori]: [
        FeatureURI.ClothingGender,
        FeatureURI.ClothingType,
    ],
    // Sports & Hobby
    [CategoryID.Biciclette]: [FeatureURI.BicycleType, FeatureURI.ItemCondition],
    // Lavoro
    [CategoryID.OfferteLavoro]: [
        FeatureURI.JobCategory,
        FeatureURI.WorkLevel,
        FeatureURI.WorkHours,
    ],
};
const itemConditionsFormattedValues = [
    'Nuovo',
    'Come nuovo',
    'Ottimo',
    'Buono',
    'Danneggiato',
];
/**
 * Filters and formats the features of an item to
 * extrapolate the main data used in item cards and details.
 *
 * `config` description:
 *  * `short`: return a short versions of the label.
 *  * `assetsBase`: base URL for subito static assets
 */
function formatMainData(item, config = {}) {
    if (!item?.features) {
        return [];
    }
    let featureKeys = MainDataFeatures[item.category?.id];
    if (!featureKeys) {
        return [];
    }
    // Limit the max number of features to show in short mode
    if (config.maxFeatures != null) {
        featureKeys = featureKeys.slice(0, config.maxFeatures);
    }
    if (item.category.id === CategoryID.Auto ||
        item.category.id === CategoryID.Moto) {
        if (item.features[FeatureURI.MileageScalar]) {
            featureKeys = featureKeys.map((feat) => {
                return feat === FeatureURI.Mileage ? FeatureURI.MileageScalar : feat;
            });
        }
    }
    return featureKeys.map((key) => {
        let label = '';
        const feature = item.features[key];
        const featureConfig = FeatureConfigurations[key];
        if (key === FeatureURI.ItemCondition) {
            itemConditionsFormattedValues.find((itemConditionsFormattedValue) => {
                if (feature !== undefined &&
                    feature?.values?.[0]?.value.indexOf(itemConditionsFormattedValue) !==
                        -1) {
                    label = itemConditionsFormattedValue;
                }
                return label;
            });
        }
        else if (feature?.values?.[0]?.value) {
            label =
                featureConfig?.formatter?.(feature, config) ??
                    feature?.values?.[0].value;
        }
        return {
            label,
            placeholder: featureConfig?.placeholder ?? '',
            iconURL: `${config.assetsBase || ''}/static/icons/features${key}.svg`,
        };
    });
}

/**
 * The locale representing the Italian language as spoken in Italy.
 */
const italianInItaly = new Intl.Locale('it-IT');

const formatDistance = (value) => {
    try {
        if (Math.abs(value) < 1000) {
            return Intl.NumberFormat(italianInItaly, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 1,
                style: 'unit',
                unit: 'meter',
                unitDisplay: 'short',
            }).format(value);
        }
        else {
            return Intl.NumberFormat(italianInItaly, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 1,
                style: 'unit',
                unit: 'kilometer',
                unitDisplay: 'short',
                useGrouping: true,
            }).format(value / 1000);
        }
    }
    catch {
        const simpleNumber = Intl.NumberFormat(italianInItaly, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
        }).format(value / 1000);
        return `${simpleNumber} km`;
    }
};

/**
 * A formatter of the Intl API which formats numbers
 * to whole numbers in the Italian format.
 * Be aware that decimals will be rounded, and .5 is rounded up.
 */
const wholeNumberDigit = Intl.NumberFormat(italianInItaly, {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: true,
});
/**
 * A formatter of the Intl API which formats numbers
 * to whole-number prices of Euros in the Italian format.
 * Be aware that decimals will be rounded, and .5 is rounded up.
 */
const wholeNumberPrice = Intl.NumberFormat(italianInItaly, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: true,
});
/**
 * A formatter of the Intl API which formats numbers
 * in the Italian format with 2 decimal places.
 */
const twoDecimalPlacesDigit = Intl.NumberFormat(italianInItaly, {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
});
/**
 * A formatter of the Intl API which formats numbers
 * to prices of Euros in the Italian format with 2 decimal places.
 */
const twoDecimalPlacesPrice = Intl.NumberFormat(italianInItaly, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
});

/**
 * Formats the date as a digit for the date and the short month ("gen", "feb", etc)
 */
const italianMonthAndDayFormatter = new Intl.DateTimeFormat(italianInItaly, {
    day: 'numeric',
    month: 'short',
});
const italianHourAndMinuteFormatter = new Intl.DateTimeFormat(italianInItaly, {
    timeStyle: 'short',
});
const isToday = (dateToCheck) => {
    const now = new Date().toLocaleDateString(italianInItaly);
    return now === dateToCheck.toLocaleDateString(italianInItaly);
};
const isYesterday = (dateToCheck) => {
    const workingDate = new Date();
    workingDate.setDate(workingDate.getDate() - 1);
    const yesterday = workingDate;
    return (yesterday.toLocaleDateString(italianInItaly) ===
        dateToCheck.toLocaleDateString(italianInItaly));
};
const isValid = (dateToCheck) => dateToCheck.toString() !== 'Invalid Date';
const formatDate = (dateToFormat) => {
    let date = new Date(dateToFormat);
    // if the parsed date is invalid we try to use a different format which should fix many iOS devices.
    // @see https://github.com/mdn/browser-compat-data/issues/15401
    if (!isValid(date)) {
        const dateTentative = new Date(dateToFormat.replace(' ', 'T'));
        if (isValid(dateTentative)) {
            date = dateTentative;
        }
    }
    const preposition = date.getHours() === 1 ? "all'" : 'alle ';
    if (isToday(date)) {
        return `Oggi ${preposition}${italianHourAndMinuteFormatter.format(date)}`;
    }
    else if (isYesterday(date)) {
        return `Ieri ${preposition}${italianHourAndMinuteFormatter.format(date)}`;
    }
    else {
        return `${italianMonthAndDayFormatter.format(date)} ${preposition}${italianHourAndMinuteFormatter.format(date)}`;
    }
};

const SUBITO_DEVICE_TYPE_HEADER = 'x-subito-device';
const MOBILE_CLIENT_HINT_HEADER = 'sec-ch-ua-mobile';
const USER_AGENT_HEADER = 'user-agent';

const isTablet = (userAgent) => userAgent?.match(/Tablet|iPad/i);
const isAndroidTablet = (userAgent) => {
    if (!userAgent) {
        return undefined;
    }
    const isAndroid = /Android/i.test(userAgent); // Check for Android
    const isMobile = /Mobile/i.test(userAgent); // Check for mobile devices
    // Android Tablet: Android but not Mobile
    return isAndroid && !isMobile;
};
const isMobile$1 = (userAgent) => userAgent?.match(/Mobile|Android|BlackBerry/i);

/**
 * Server-side check for getting the device type.
 * It checks, in order:
 *   - `x-subito-device` header, set by Akamai
 *   - `sec-ch-ua-mobile` client-hint
 *   - `user-agent` header
 */
function getDeviceType(req) {
    if (!req) {
        return null;
    }
    // Try akamai header
    const akamaiDeviceType = req.headers[SUBITO_DEVICE_TYPE_HEADER];
    if (akamaiDeviceType) {
        return akamaiDeviceType;
    }
    // Try Client Hints header
    // https://wicg.github.io/ua-client-hints/#sec-ch-ua-mobile
    const mobileClientHint = req.headers[MOBILE_CLIENT_HINT_HEADER];
    // Value '?0' could both mean 'tablet' and 'desktop'
    if (mobileClientHint === '?1') {
        return 'mobile';
    }
    // Fall back to user agent
    const userAgent = req.headers[USER_AGENT_HEADER];
    // We don't have any data, we return null
    if (!userAgent) {
        return null;
    }
    if (isTablet(userAgent) || isAndroidTablet(userAgent)) {
        return 'tablet';
    }
    else if (isMobile$1(userAgent)) {
        return 'mobile';
    }
    else {
        return 'desktop';
    }
}

/*
 * This function is used to get the device type from the request headers(Server-side).
 * The implementation is based on the logic we have in the getDeviceType except for the parameter.
 * To be compliant with the app router, we need to pass the headers object instead of the request.
 * It checks, in order:
 *   - `x-subito-device` header, set by Akamai
 *   - `sec-ch-ua-mobile` client-hint
 *   - `user-agent` header
 */
const getDeviceTypeAppRouter = (headers) => {
    const akamaiDeviceType = headers.get(SUBITO_DEVICE_TYPE_HEADER);
    if (akamaiDeviceType) {
        return akamaiDeviceType;
    }
    // Try Client Hints header
    // https://wicg.github.io/ua-client-hints/#sec-ch-ua-mobile
    const mobileClientHint = headers.get(MOBILE_CLIENT_HINT_HEADER);
    // Value '?0' could both mean 'tablet' and 'desktop'
    if (mobileClientHint === '?1') {
        return 'mobile';
    }
    // Fall back to user agent
    const userAgent = headers.get(USER_AGENT_HEADER);
    if (!userAgent) {
        return null;
    }
    if (isTablet(userAgent) || isAndroidTablet(userAgent)) {
        return 'tablet';
    }
    else if (isMobile$1(userAgent)) {
        return 'mobile';
    }
    else {
        return 'desktop';
    }
};

const getDomainByEnv = (env) => {
    switch (env) {
        case 'dev':
            return '.regresssubito.it';
        case 'stage':
            return '.piutardi.it';
        case 'prod':
            return '.subito.it';
        default:
            assertNever(env);
    }
};

const STORAGE_KEY = 'PULSE_HOUSTON_COLLECTOR';
/**
 * Fetches collector configuration from the remote endpoint
 */
const fetchCollector = async (env) => {
    const collectorPath = `https://www.${env === 'prod' ? 'subito' : 'piutardi'}.it/config/collector.json`;
    const response = await fetch(collectorPath);
    if (!response.ok) {
        throw new Error(`Failed to fetch collector config: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    // Store in cache
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
    catch (error) {
        console.warn('Error storing collector data:', error);
    }
    return data.default;
};
/**
 * Gets the Houston collector configuration with caching
 * Implements stale-while-revalidate pattern
 */
const getHoustonCollector = async (env) => {
    // Default values to use if everything fails
    const defaultCollector = {
        datacollector: 'https://collector.mpianalytics.com/api/v1/track',
        cis: 'https://cis.mpianalytics.com',
    };
    try {
        // Check for cached data
        const cachedItem = localStorage.getItem(STORAGE_KEY);
        if (cachedItem) {
            try {
                // Return cached data immediately
                const cachedData = JSON.parse(cachedItem);
                // Refresh cache in background without awaiting
                fetchCollector(env).catch((error) => {
                    console.warn('Background cache refresh failed:', error);
                });
                return cachedData.default;
            }
            catch (error) {
                console.warn('Error parsing stored collector:', error);
                // Continue to fetch fresh data
            }
        }
        // No valid cache, fetch fresh data
        return await fetchCollector(env);
    }
    catch (error) {
        console.error('Error getting Houston collector:', error);
        return defaultCollector;
    }
};

const failOnServer = () => {
    if (!isBrowser) {
        throw new Error('This function can only be called on the browser. Please add a check to your code.');
    }
};

const getNavigationType = () => {
    failOnServer();
    // This path will work on Chrome and Firefox
    if ('getEntriesByType' in window.performance) {
        const navigationEvent = window.performance.getEntriesByType('navigation')[0];
        if (navigationEvent != null && 'type' in navigationEvent) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return navigationEvent.type;
        }
    }
    const legacyNavigationType = window.performance.navigation.type;
    switch (legacyNavigationType) {
        case window.performance.navigation.TYPE_NAVIGATE: {
            return 'navigate';
        }
        case window.performance.navigation.TYPE_RELOAD: {
            return 'reload';
        }
        case window.performance.navigation.TYPE_BACK_FORWARD: {
            return 'back_forward';
        }
        default: {
            return 'reserved';
        }
    }
};
const loadedFromNavigation = () => getNavigationType() === 'navigate';
const loadedFromReload = () => getNavigationType() === 'reload';
const loadedFromHistoryNavigation = () => getNavigationType() === 'back_forward';

// This const is exported both from sbt-web/middleware and sbt-web/auth but,
// to avoid to add another peer dependency, I added the const also here.
const LABEL_USER_ID = 'x-subito-user-id';
const getRequestInfoAppRouter = (headers, cookieStore) => {
    const requestID = headers.get(LABEL_REQUEST_ID) ?? crypto.randomUUID();
    const bottega = cookieStore.get('bottega')?.value ?? '';
    const userID = headers.get(LABEL_USER_ID) ?? '';
    return {
        userID,
        sbtHeaders: {
            requestID,
            bottega,
        },
    };
};

/**
 * Fetches the correct shop type to show for professional sellers.
 * @param id The ID of the category to search for.
 * @returns The string of the format, which can be empty.
 */
const getShopLabelByCategoryId = (id) => {
    switch (id) {
        case CategoryID.Appartamenti:
        case CategoryID.CamerePostiLetto:
        case CategoryID.Ville:
        case CategoryID.TerreniRustici:
        case CategoryID.GarageBox:
        case CategoryID.LoftMansarde:
        case CategoryID.CaseVacanza:
        case CategoryID.UfficiLocali:
            return 'Agenzia';
        case CategoryID.OfferteLavoro:
            return 'Azienda';
        case CategoryID.Servizi:
        case CategoryID.Candidati:
        case CategoryID.Attrezzature:
            return '';
        default:
            return 'Rivenditore';
    }
};

/**
 * This function determines whether a given HTML element has a CSS class or not.
 */
function hasNodeClass(element, CSSClass) {
    assertIsNodeInstance(element);
    return element.classList.contains(CSSClass);
}

/**
 * This function adds the `display: none` property to a given HTML element.
 */
function hideNode(element) {
    assertIsNodeInstance(element);
    element.style.display = 'none';
}

/**
 * This function determines whether an object is empty or not.
 */
function isEmpty(obj) {
    return Object.keys(obj).length === 0 && obj.constructor === Object;
}

/**
 * This function returns true if the user agent is a mobile or tablet device
 * @deprecated window.matchMedia or the useViewport hook should be used instead.
 */
function isMobile(userAgent = navigator?.userAgent) {
    if (!userAgent) {
        return true;
    }
    return !!userAgent.match(/Mobile|Android|iPad|Tablet|BlackBerry/i);
}
/**
 * This function returns true if the isMobile function returns false
 * @deprecated window.matchMedia or the useViewport hook should be used instead.
 */
function isDesktop(userAgent = navigator.userAgent) {
    return !isMobile(userAgent);
}

const isShippable = (ad) => {
    const advertiser = ad.advertiser;
    const isAdShippable = evalBoolFeature(ad.features[FeatureURI.ItemShippable]);
    const isPro = advertiser.type === AdvertiserType.Pro;
    const isAProWithTransactions = isPro && !!advertiser.proTransactionsEnabled;
    return isAdShippable && (!isPro || isAProWithTransactions);
};

function getExtension(url) {
    const b = url.split('?')[0];
    return b.substr(b.lastIndexOf('.') + 1);
}
function loadJS(params, isAnonymous) {
    const element = document.createElement('script');
    const head = document.getElementsByTagName('head')[0];
    const promise = new Promise((resolve) => {
        element.addEventListener('load', () => resolve(), false);
    });
    element.src = params.url;
    if (isAnonymous) {
        element.crossOrigin = 'anonymous';
    }
    head.appendChild(element);
    return { element, promise };
}
function loadCSS(params, isAnonymous) {
    const link = document.createElement('link');
    const head = document.getElementsByTagName('head')[0];
    link.href = params.url;
    link.rel = 'stylesheet';
    if (isAnonymous) {
        link.crossOrigin = 'anonymous';
    }
    // Technique to force non-blocking loading from:
    // https://github.com/filamentgroup/loadCSS/blob/master/src/loadCSS.js#L25
    link.media = 'only x';
    link.type = 'text/css';
    // On next tick, just set the media to what it's supposed to be
    setTimeout(() => {
        link.media = 'all';
    });
    const promise = new Promise((r) => {
        link.addEventListener('load', () => r(), false);
    });
    head.appendChild(link);
    return { element: link, promise };
}
function loadAsync(params) {
    const extension = getExtension(params.url);
    const isAnonymous = params.assetBase !== undefined &&
        params.url.indexOf(params.assetBase) === 0;
    switch (extension) {
        case 'js':
            return loadJS(params, isAnonymous).promise;
        case 'css':
            return loadCSS(params, isAnonymous).promise;
        default:
            return Promise.reject();
    }
}

var LogLevel;
(function (LogLevel) {
    LogLevel["Info"] = "INFO";
    LogLevel["Warn"] = "WARN";
    LogLevel["Error"] = "ERROR";
})(LogLevel || (LogLevel = {}));
class Logger {
    config;
    constructor(config) {
        this.config = config;
    }
    logInfo(msg, request_id) {
        console.log(this.formatLog({
            msg,
            level: LogLevel.Info,
            request_id: request_id || getRequestId(),
        }));
    }
    logError(msg, request_id) {
        console.log(this.formatLog({
            msg,
            level: LogLevel.Error,
            request_id: request_id || getRequestId(),
        }));
    }
    logWarn(msg, request_id) {
        console.log(this.formatLog({
            msg,
            level: LogLevel.Warn,
            request_id: request_id || getRequestId(),
        }));
    }
    log(logObject) {
        console.log(this.formatLog(logObject));
    }
    formatLog(logObject) {
        return JSON.stringify({
            ...this.config,
            ...logObject,
            timestamp: new Date().toISOString(),
        });
    }
}

let logger;
const getLogger = function (config) {
    if (!logger) {
        logger = new Logger(config);
    }
    return logger;
};

/// <reference types="@sbt-web/global-types" />
const LAST_SURVEY = 'kampyle_expire';
const SURVEY_EXPIRE_DAYS = 40;
const MEDALLIA_IAB_ID = 'c:medallia-ihzryFLY';
function getMedalliaSurvey(medalliaFormId) {
    // Check if there is a survey to show and if is not showed any survey in the past SURVEY_EXPIRE_DAYS
    function isAlreadyShow() {
        let lastSurvey = null;
        try {
            lastSurvey = window.localStorage.getItem(LAST_SURVEY);
            return (((!lastSurvey || new Date() > new Date(Number(lastSurvey))) &&
                medalliaFormId &&
                window.KAMPYLE_ONSITE_SDK &&
                window.KAMPYLE_ONSITE_SDK.loadForm(medalliaFormId)));
        }
        catch {
            console.error('Error in checking being able to show a survey. Defaulting to not showing the survey.');
            return true;
        }
    }
    // Verify if cookie consent has been accepted
    const consent = window.Didomi.getUserConsentStatus('cookies', MEDALLIA_IAB_ID);
    if (isAlreadyShow() && consent) {
        const expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + SURVEY_EXPIRE_DAYS);
        // SET SURVEY_EXPIRE_DAYS days expiration survey
        window.localStorage.setItem(LAST_SURVEY, expirationDate.getTime().toString());
        window.KAMPYLE_ONSITE_SDK.showForm(medalliaFormId);
    }
}

/**
 * @deprecated Use `jest.fn()` in tests.
 */
const noop = () => undefined;

/**
 * @description
 * used in getServerSideProps to fix undefined element, converting in a "strict" json.
 * See:
 * - https://github.com/vercel/next.js/discussions/11209
 * - https://github.com/vercel/next.js/issues/11993#issuecomment-617474952
 * @param obj
 */
const isObject = (o) => o === Object(o) && typeof o !== 'function';
const toNullifiedObject = (acc, [key, value]) => {
    acc[key] = nullify(value);
    return acc;
};
const nullify = (obj) => Array.isArray(obj)
    ? obj.map(nullify)
    : isObject(obj)
        ? Object.entries(obj).reduce(toNullifiedObject, {})
        : typeof obj === 'undefined'
            ? null
            : obj;
const nullifyUndefinedProperties = (obj) => nullify(obj);

/**
 * This function accepts a callback that gets executed once the DOM
 * is interactive or immediately if page is already interactive.
 */
function onDOMContentLoaded(cb) {
    if (document.readyState === 'interactive' ||
        document.readyState === 'complete') {
        cb();
    }
    else {
        window.addEventListener('DOMContentLoaded', cb);
    }
}

/**
 * This function accepts a callback that gets executed once the page loads or
 * immediately if page is already loaded.
 */
function onReadyState(cb) {
    if (document.readyState === 'complete') {
        cb();
    }
    else {
        window.addEventListener('load', cb);
    }
}

/**
 * This function parses a given HTML form creating an array of NameValuePairs.
 */
function parseForm(form) {
    assertIsNodeInstance(form);
    const parsedForm = [];
    const formElements = form.elements;
    for (let key = 0; key < formElements.length; key++) {
        const element = formElements[key];
        let value = element.value;
        if (value === 'false' || value === 'true') {
            value = !!JSON.parse(value.toLowerCase());
        }
        switch (element.type) {
            case 'text':
            case 'email':
            case 'radio':
            case 'hidden':
            case 'textarea':
            case 'select-one':
                parsedForm.push({
                    name: element.name,
                    value,
                });
                break;
            case 'checkbox':
                parsedForm.push({
                    name: element.name,
                    value: element.checked,
                });
                break;
        }
    }
    return parsedForm;
}

const decimalSeparator = ',';
const thousandSeparator = '.';
/**
 * Parses a string containing an italian-formatted number (e.g. "123.456,78")
 */
function parseItalianNumber(numberToParse) {
    const formattedNumber = numberToParse
        ?.replace(new RegExp(`\\${thousandSeparator}`, 'g'), '')
        .replace(new RegExp(`\\${decimalSeparator}`), '.');
    return parseFloat(formattedNumber);
}

const createPromiseResolver = () => {
    let resolve;
    const promise = new Promise((res) => {
        resolve = res;
    });
    // @ts-expect-error - resolve is set in the promise constructor
    return [promise, resolve];
};

const isValidEnvironment = (env) => env != undefined && ['dev', 'stage', 'prod'].includes(env);
/**
 * Refines the type of the environment variable for the deploy environment into the acceptable strings.
 * In the case that the environment is not refinable, this function will throw an error.
 * @param e The environment string to refine.
 * @returns Returns the input but with a refined type.
 */
const refineEnvironment = (e) => {
    if (isValidEnvironment(e)) {
        return e;
    }
    else {
        throw new RangeError(`The value ${e ?? 'undefined'} given as an environment is not valid. The environment can only be dev, stage or prod.`);
    }
};

/**
 * This function removes a CSS class to a given HTML element.
 */
function removeNodeClass(element, CSSClass) {
    assertIsNodeInstance(element);
    element.classList.remove(CSSClass);
}

const shouldReduceDataUse = ({ saveData = null, effectiveConnectionType = null, deviceMemory = null, }) => {
    const saveDataSignal = saveData ?? false;
    const ectSignal = effectiveConnectionType ?? '4g';
    const deviceMemorySignal = deviceMemory ?? Infinity;
    return saveDataSignal || ectSignal !== '4g' || deviceMemorySignal <= 2;
};

/**
 * This function adds the `display: block` property to a given HTML element.
 */
function showNode(element) {
    assertIsNodeInstance(element);
    element.style.display = 'block';
}

/**
 * stripVowelAccent
 *
 * @private
 */
function stripVowelAccent(rawString) {
    const rExps = [
        {
            re: /[\xC0-\xC6]/g,
            ch: 'A',
        },
        {
            re: /[\xE0-\xE6]/g,
            ch: 'a',
        },
        {
            re: /[\xC8-\xCB]/g,
            ch: 'E',
        },
        {
            re: /[\xE8-\xEB]/g,
            ch: 'e',
        },
        {
            re: /[\xCC-\xCF]/g,
            ch: 'I',
        },
        {
            re: /[\xEC-\xEF]/g,
            ch: 'i',
        },
        {
            re: /[\xD2-\xD6]/g,
            ch: 'O',
        },
        {
            re: /[\xF2-\xF6]/g,
            ch: 'o',
        },
        {
            re: /[\xD9-\xDC]/g,
            ch: 'U',
        },
        {
            re: /[\xF9-\xFC]/g,
            ch: 'u',
        },
        {
            re: /[\xD1]/g,
            ch: 'N',
        },
        {
            re: /[\xF1]/g,
            ch: 'n',
        },
        {
            re: /Ç/g,
            ch: 'C',
        },
        {
            re: /ç/g,
            ch: 'c',
        },
    ];
    for (let i = 0, len = rExps.length; i < len; i = i + 1) {
        rawString = rawString.replace(rExps[i].re, rExps[i].ch);
    }
    return rawString;
}

/**
 * This function applies Subito slugification rules to a given string.
 */
function slugify(rawString) {
    const classMark = 
    // eslint-disable-next-line no-control-regex,no-misleading-character-class
    /(?:[\0-\x1F\x7F-\x9F\xAA\xAD\xBA\u01BB\u01C0-\u01C3\u0294\u0300-\u036F\u0378\u0379\u0380-\u0383\u038B\u038D\u03A2\u0483-\u0489\u0530\u0557\u0558\u0560\u0588\u058B\u058C\u0590-\u05BD\u05BF\u05C1\u05C2\u05C4\u05C5\u05C7-\u05F2\u05F5-\u0605\u0610-\u061A\u061C\u061D\u0620-\u063F\u0641-\u065F\u066E-\u06D3\u06D5-\u06DD\u06DF-\u06E4\u06E7\u06E8\u06EA-\u06EF\u06FA-\u06FC\u06FF\u070E-\u07BF\u07CA-\u07F3\u07FB-\u0819\u081B-\u0823\u0825-\u0827\u0829-\u082F\u083F-\u085D\u085F-\u0963\u0972-\u09E5\u09F0\u09F1\u09FC-\u0A65\u0A70-\u0AE5\u0AF2-\u0B65\u0B71\u0B78-\u0BE5\u0BFB-\u0C65\u0C70-\u0C77\u0C80-\u0CE5\u0CF0-\u0D4E\u0D50-\u0D57\u0D5F-\u0D65\u0D7A-\u0DE5\u0DF0-\u0DF3\u0DF5-\u0E3E\u0E40-\u0E45\u0E47-\u0E4E\u0E5C-\u0EC5\u0EC7-\u0ECF\u0EDA-\u0F00\u0F18\u0F19\u0F35\u0F37\u0F39\u0F3E-\u0F84\u0F86-\u0FBD\u0FC6\u0FCD\u0FDB-\u103F\u1050-\u108F\u109A-\u109D\u10C6\u10C8-\u10CC\u10CE-\u10FA\u10FD-\u135F\u137D-\u138F\u139A-\u139F\u13F6\u13F7\u13FE\u13FF\u1401-\u166C\u166F-\u167F\u1681-\u169A\u169D-\u16EA\u16F1-\u1734\u1737-\u17D3\u17DC-\u17DF\u17EA-\u17EF\u17FA-\u17FF\u180B-\u180F\u181A-\u1842\u1844-\u193F\u1941-\u1943\u1950-\u19CF\u19DB-\u19DD\u1A00-\u1A1D\u1A20-\u1A7F\u1A8A-\u1A8F\u1A9A-\u1A9F\u1AAE-\u1B4F\u1B6B-\u1B73\u1B7D-\u1BAF\u1BBA-\u1BFB\u1C00-\u1C3A\u1C4A-\u1C4F\u1C5A-\u1C77\u1C89-\u1CBF\u1CC8-\u1CD2\u1CD4-\u1CFF\u1DC0-\u1DFF\u1F16\u1F17\u1F1E\u1F1F\u1F46\u1F47\u1F4E\u1F4F\u1F58\u1F5A\u1F5C\u1F5E\u1F7E\u1F7F\u1FB5\u1FC5\u1FD4\u1FD5\u1FDC\u1FF0\u1FF1\u1FF5\u1FFF\u200B-\u200F\u202A-\u202E\u2060-\u206F\u2072\u2073\u208F\u209D-\u209F\u20BF-\u20FF\u2135-\u2138\u218C-\u218F\u23FF\u2427-\u243F\u244B-\u245F\u2B74\u2B75\u2B96\u2B97\u2BBA-\u2BBC\u2BC9\u2BD2-\u2BEB\u2BF0-\u2BFF\u2C2F\u2C5F\u2CEF-\u2CF1\u2CF4-\u2CF8\u2D26\u2D28-\u2D2C\u2D2E-\u2D6E\u2D71-\u2DFF\u2E45-\u2E7F\u2E9A\u2EF4-\u2EFF\u2FD6-\u2FEF\u2FFC-\u2FFF\u3006\u302A-\u302F\u303C\u3040-\u309A\u309F\u30A1-\u30FA\u30FF-\u318F\u31A0-\u31BF\u31E4-\u31FF\u321F\u32FF\u3400-\u4DBF\u4E00-\uA014\uA016-\uA48F\uA4C7-\uA4F7\uA500-\uA60B\uA610-\uA61F\uA62A-\uA63F\uA66E-\uA672\uA674-\uA67D\uA69E-\uA6E5\uA6F0\uA6F1\uA6F8-\uA6FF\uA78F\uA7AF\uA7B8-\uA7F7\uA7FB-\uA827\uA82C-\uA82F\uA83A-\uA873\uA878-\uA8CD\uA8DA-\uA8F7\uA8FB\uA8FD-\uA8FF\uA90A-\uA92D\uA930-\uA95E\uA960-\uA9C0\uA9CE\uA9DA-\uA9DD\uA9E0-\uA9E5\uA9E7-\uA9EF\uA9FA-\uAA4F\uAA5A\uAA5B\uAA60-\uAA6F\uAA71-\uAA76\uAA7A-\uAADC\uAAE0-\uAAEF\uAAF2\uAAF5-\uAB2F\uAB66-\uAB6F\uABC0-\uABEA\uABEC-\uABEF\uABFA-\uD7FF\uE000-\uFAFF\uFB07-\uFB12\uFB18-\uFB28\uFB2A-\uFBB1\uFBC2-\uFD3D\uFD40-\uFDFB\uFDFE-\uFE0F\uFE1A-\uFE2F\uFE53\uFE67\uFE6C-\uFF00\uFF66-\uFF6F\uFF71-\uFF9D\uFFA0-\uFFDF\uFFE7\uFFEF-\uFFFB\uFFFE\uFFFF]|\uD800[\uDC00-\uDCFF\uDD03-\uDD06\uDD34-\uDD36\uDD8F\uDD9C-\uDD9F\uDDA1-\uDDCF\uDDFD-\uDEE0\uDEFC-\uDF1F\uDF24-\uDF40\uDF42-\uDF49\uDF4B-\uDF9E\uDFA0-\uDFCF\uDFD6-\uDFFF]|\uD801[\uDC50-\uDC9F\uDCAA-\uDCAF\uDCD4-\uDCD7\uDCFC-\uDD6E\uDD70-\uDFFF]|\uD802[\uDC00-\uDC56\uDC60-\uDC76\uDC80-\uDCA6\uDCB0-\uDCFA\uDD00-\uDD15\uDD1C-\uDD1E\uDD20-\uDD3E\uDD40-\uDDBB\uDDBE\uDDBF\uDDD0\uDDD1\uDE00-\uDE3F\uDE48-\uDE4F\uDE59-\uDE7C\uDE80-\uDE9C\uDEA0-\uDEC7\uDEC9-\uDEEA\uDEF7-\uDF38\uDF40-\uDF57\uDF60-\uDF77\uDF80-\uDF98\uDF9D-\uDFA8\uDFB0-\uDFFF]|\uD803[\uDC00-\uDC7F\uDCB3-\uDCBF\uDCF3-\uDCF9\uDD00-\uDE5F\uDE7F-\uDFFF]|\uD804[\uDC00-\uDC46\uDC4E-\uDC51\uDC70-\uDCBA\uDCBD\uDCC2-\uDCEF\uDCFA-\uDD35\uDD44-\uDD73\uDD76-\uDDC4\uDDCA-\uDDCC\uDDCE\uDDCF\uDDDA\uDDDC\uDDE0\uDDF5-\uDE37\uDE3E-\uDEA8\uDEAA-\uDEEF\uDEFA-\uDFFF]|\uD805[\uDC00-\uDC4A\uDC5A\uDC5C\uDC5E-\uDCC5\uDCC7-\uDCCF\uDCDA-\uDDC0\uDDD8-\uDE40\uDE44-\uDE4F\uDE5A-\uDE5F\uDE6D-\uDEBF\uDECA-\uDF2F\uDF40-\uDFFF]|\uD806[\uDC00-\uDC9F\uDCF3-\uDFFF]|\uD807[\uDC00-\uDC40\uDC46-\uDC4F\uDC6D-\uDC6F\uDC72-\uDFFF]|[\uD808\uD80A-\uD819\uD81C-\uD82E\uD830-\uD833\uD837-\uD839\uD83F-\uDBFF][\uDC00-\uDFFF]|\uD809[\uDC6F\uDC75-\uDFFF]|\uD81A[\uDC00-\uDE5F\uDE6A-\uDE6D\uDE70-\uDEF4\uDEF6-\uDF36\uDF46-\uDF4F\uDF5A\uDF62-\uDFFF]|\uD81B[\uDC00-\uDF92\uDFA0-\uDFDF\uDFE1-\uDFFF]|\uD82F[\uDC00-\uDC9B\uDC9D\uDC9E\uDCA0-\uDFFF]|\uD834[\uDCF6-\uDCFF\uDD27\uDD28\uDD65-\uDD69\uDD6D-\uDD82\uDD85-\uDD8B\uDDAA-\uDDAD\uDDE9-\uDDFF\uDE42-\uDE44\uDE46-\uDEFF\uDF57-\uDF5F\uDF72-\uDFFF]|\uD835[\uDC55\uDC9D\uDCA0\uDCA1\uDCA3\uDCA4\uDCA7\uDCA8\uDCAD\uDCBA\uDCBC\uDCC4\uDD06\uDD0B\uDD0C\uDD15\uDD1D\uDD3A\uDD3F\uDD45\uDD47-\uDD49\uDD51\uDEA6\uDEA7\uDFCC\uDFCD]|\uD836[\uDE00-\uDE36\uDE3B-\uDE6C\uDE75\uDE84\uDE8C-\uDFFF]|\uD83A[\uDC00-\uDCC6\uDCD0-\uDCFF\uDD44-\uDD4F\uDD5A-\uDD5D\uDD60-\uDFFF]|\uD83B[\uDC00-\uDEEF\uDEF2-\uDFFF]|\uD83C[\uDC2C-\uDC2F\uDC94-\uDC9F\uDCAF\uDCB0\uDCC0\uDCD0\uDCF6-\uDCFF\uDD0D-\uDD0F\uDD2F\uDD6C-\uDD6F\uDDAD-\uDDE5\uDE03-\uDE0F\uDE3C-\uDE3F\uDE49-\uDE4F\uDE52-\uDEFF]|\uD83D[\uDED3-\uDEDF\uDEED-\uDEEF\uDEF7-\uDEFF\uDF74-\uDF7F\uDFD5-\uDFFF]|\uD83E[\uDC0C-\uDC0F\uDC48-\uDC4F\uDC5A-\uDC5F\uDC88-\uDC8F\uDCAE-\uDD0F\uDD1F\uDD28-\uDD2F\uDD31\uDD32\uDD3F\uDD4C-\uDD4F\uDD5F-\uDD7F\uDD92-\uDDBF\uDDC1-\uDFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF])/g;
    const classSpace = /(?:[ -/:-@[-`{-~\xA0-\xA9\xAB\xAC\xAE-\xB1\xB4\xB6-\xB8\xBB\xBF\xD7\xF7\u02C2-\u02C5\u02D2-\u02DF\u02E5-\u02EB\u02ED\u02EF-\u02FF\u0375\u037E\u0384\u0385\u0387\u03F6\u0482\u055A-\u055F\u0589\u058A\u058D-\u058F\u05BE\u05C0\u05C3\u05C6\u05F3\u05F4\u0606-\u060F\u061B\u061E\u061F\u066A-\u066D\u06D4\u06DE\u06E9\u06FD\u06FE\u0700-\u070D\u07F6-\u07F9\u0830-\u083E\u085E\u0964\u0965\u0970\u09F2\u09F3\u09FA\u09FB\u0AF0\u0AF1\u0B70\u0BF3-\u0BFA\u0C7F\u0D4F\u0D79\u0DF4\u0E3F\u0E4F\u0E5A\u0E5B\u0F01-\u0F17\u0F1A-\u0F1F\u0F34\u0F36\u0F38\u0F3A-\u0F3D\u0F85\u0FBE-\u0FC5\u0FC7-\u0FCC\u0FCE-\u0FDA\u104A-\u104F\u109E\u109F\u10FB\u1360-\u1368\u1390-\u1399\u1400\u166D\u166E\u1680\u169B\u169C\u16EB-\u16ED\u1735\u1736\u17D4-\u17D6\u17D8-\u17DB\u1800-\u180A\u1940\u1944\u1945\u19DE-\u19FF\u1A1E\u1A1F\u1AA0-\u1AA6\u1AA8-\u1AAD\u1B5A-\u1B6A\u1B74-\u1B7C\u1BFC-\u1BFF\u1C3B-\u1C3F\u1C7E\u1C7F\u1CC0-\u1CC7\u1CD3\u1FBD\u1FBF-\u1FC1\u1FCD-\u1FCF\u1FDD-\u1FDF\u1FED-\u1FEF\u1FFD\u1FFE\u2000-\u200A\u2010-\u2029\u202F-\u205F\u207A-\u207E\u208A-\u208E\u20A0-\u20BE\u2100\u2101\u2103-\u2106\u2108\u2109\u2114\u2116-\u2118\u211E-\u2123\u2125\u2127\u2129\u212E\u213A\u213B\u2140-\u2144\u214A-\u214D\u214F\u218A\u218B\u2190-\u23FE\u2400-\u2426\u2440-\u244A\u249C-\u24E9\u2500-\u2775\u2794-\u2B73\u2B76-\u2B95\u2B98-\u2BB9\u2BBD-\u2BC8\u2BCA-\u2BD1\u2BEC-\u2BEF\u2CE5-\u2CEA\u2CF9-\u2CFC\u2CFE\u2CFF\u2D70\u2E00-\u2E2E\u2E30-\u2E44\u2E80-\u2E99\u2E9B-\u2EF3\u2F00-\u2FD5\u2FF0-\u2FFB\u3000-\u3004\u3008-\u3020\u3030\u3036\u3037\u303D-\u303F\u309B\u309C\u30A0\u30FB\u3190\u3191\u3196-\u319F\u31C0-\u31E3\u3200-\u321E\u322A-\u3247\u3250\u3260-\u327F\u328A-\u32B0\u32C0-\u32FE\u3300-\u33FF\u4DC0-\u4DFF\uA490-\uA4C6\uA4FE\uA4FF\uA60D-\uA60F\uA673\uA67E\uA6F2-\uA6F7\uA700-\uA716\uA720\uA721\uA789\uA78A\uA828-\uA82B\uA836-\uA839\uA874-\uA877\uA8CE\uA8CF\uA8F8-\uA8FA\uA8FC\uA92E\uA92F\uA95F\uA9C1-\uA9CD\uA9DE\uA9DF\uAA5C-\uAA5F\uAA77-\uAA79\uAADE\uAADF\uAAF0\uAAF1\uAB5B\uABEB\uFB29\uFBB2-\uFBC1\uFD3E\uFD3F\uFDFC\uFDFD\uFE10-\uFE19\uFE30-\uFE52\uFE54-\uFE66\uFE68-\uFE6B\uFF01-\uFF0F\uFF1A-\uFF20\uFF3B-\uFF40\uFF5B-\uFF65\uFFE0-\uFFE6\uFFE8-\uFFEE\uFFFC\uFFFD]|\uD800[\uDD00-\uDD02\uDD37-\uDD3F\uDD79-\uDD89\uDD8C-\uDD8E\uDD90-\uDD9B\uDDA0\uDDD0-\uDDFC\uDF9F\uDFD0]|\uD801\uDD6F|\uD802[\uDC57\uDC77\uDC78\uDD1F\uDD3F\uDE50-\uDE58\uDE7F\uDEC8\uDEF0-\uDEF6\uDF39-\uDF3F\uDF99-\uDF9C]|\uD804[\uDC47-\uDC4D\uDCBB\uDCBC\uDCBE-\uDCC1\uDD40-\uDD43\uDD74\uDD75\uDDC5-\uDDC9\uDDCD\uDDDB\uDDDD-\uDDDF\uDE38-\uDE3D\uDEA9]|\uD805[\uDC4B-\uDC4F\uDC5B\uDC5D\uDCC6\uDDC1-\uDDD7\uDE41-\uDE43\uDE60-\uDE6C\uDF3C-\uDF3F]|\uD807[\uDC41-\uDC45\uDC70\uDC71]|\uD809[\uDC70-\uDC74]|\uD81A[\uDE6E\uDE6F\uDEF5\uDF37-\uDF3F\uDF44\uDF45]|\uD82F[\uDC9C\uDC9F]|\uD834[\uDC00-\uDCF5\uDD00-\uDD26\uDD29-\uDD64\uDD6A-\uDD6C\uDD83\uDD84\uDD8C-\uDDA9\uDDAE-\uDDE8\uDE00-\uDE41\uDE45\uDF00-\uDF56]|\uD835[\uDEC1\uDEDB\uDEFB\uDF15\uDF35\uDF4F\uDF6F\uDF89\uDFA9\uDFC3]|\uD836[\uDC00-\uDDFF\uDE37-\uDE3A\uDE6D-\uDE74\uDE76-\uDE83\uDE85-\uDE8B]|\uD83A[\uDD5E\uDD5F]|\uD83B[\uDEF0\uDEF1]|\uD83C[\uDC00-\uDC2B\uDC30-\uDC93\uDCA0-\uDCAE\uDCB1-\uDCBF\uDCC1-\uDCCF\uDCD1-\uDCF5\uDD10-\uDD2E\uDD30-\uDD6B\uDD70-\uDDAC\uDDE6-\uDE02\uDE10-\uDE3B\uDE40-\uDE48\uDE50\uDE51\uDF00-\uDFFF]|\uD83D[\uDC00-\uDED2\uDEE0-\uDEEC\uDEF0-\uDEF6\uDF00-\uDF73\uDF80-\uDFD4]|\uD83E[\uDC00-\uDC0B\uDC10-\uDC47\uDC50-\uDC59\uDC60-\uDC87\uDC90-\uDCAD\uDD10-\uDD1E\uDD20-\uDD27\uDD30\uDD33-\uDD3E\uDD40-\uDD4B\uDD50-\uDD5E\uDD80-\uDD91\uDDC0])+/g;
    const final = /(^-|-$)/g;
    return stripVowelAccent(rawString)
        .toLowerCase()
        .replace(classMark, '')
        .replace(classSpace, '-')
        .replace(final, '');
}

const readFromStore = (store, storageKey, initialValue) => {
    try {
        const storedValue = store.getItem(storageKey);
        if (storedValue !== null) {
            return JSON.parse(storedValue);
        }
        else if (initialValue) {
            store.setItem(storageKey, JSON.stringify(initialValue));
            return initialValue;
        }
    }
    catch (e) {
        // If user is in private mode or has storage restriction
        // localStorage can throw. JSON.parse and JSON.stringify
        // can throw too.
        console.error('Error in readFromStore', e);
        return initialValue;
    }
};
const readFromLocalStorage = (storageKey, initialValue) => {
    if (isBrowser()) {
        return readFromStore(localStorage, storageKey, initialValue);
    }
    else {
        return initialValue;
    }
};
const readFromSessionStorage = (storageKey, initialValue) => {
    if (isBrowser()) {
        return readFromStore(sessionStorage, storageKey, initialValue);
    }
    else {
        return initialValue;
    }
};

/**
 * This function allows to generate a templater function whose only parameter
 * will be an object that should contain the same keys specified in the initial
 * template string.
 *
 * Usage:
 *
 * ```js
 * const tmpl = templater`<div>${'foo'}</div>`;
 *
 * tmpl({ foo: 'bar' });
 * > "<div>bar</div>"
 * ```
 */
function templater(strings, ...keys) {
    return function (data) {
        const temp = strings.slice();
        keys.forEach((key, i) => {
            temp[i] = temp[i] + data[key];
        });
        return temp.join('');
    };
}

const unicodeSlice = (text, start, end) => [...text].slice(start, end).join('');

const pattern = /id:ad:(?<adId>[\w-]*):list:(?<contentId>[\w-]*)/;
const urnTransformer = (urn) => {
    const matcher = pattern.exec(urn);
    if (typeof matcher?.groups?.adId === 'string' &&
        typeof matcher?.groups?.contentId === 'string') {
        return {
            adId: matcher.groups.adId,
            contentId: matcher.groups.contentId,
        };
    }
    else {
        return undefined;
    }
};
const getAdId = (urn) => urnTransformer(urn)?.adId;
const getListId = (urn) => urnTransformer(urn)?.contentId;

export { LABEL_USER_ID, LogLevel, Logger, addDotsToNumber, addNodeClass, assertNever, capitalize, createPromiseResolver, debounce, dispatchEventForClients, dispatchExternalEvent, escapeRegExp, evalBoolFeature, extractDomainFromHostname, extractUtmFromLocation, extractUtmFromSearchParams, formatDistance, formatMainData, formatDate as formatPublicationDate, generateEventID, getAdId, getEnvironmentIdFromCookie as getClientsideEnvironmentId, getCookie, getDeviceType, getDeviceTypeAppRouter, getDomainByEnv, getHoustonCollector, getListId, getLogger, getMedalliaSurvey, getRequestInfoAppRouter, getSafeHTML, getShopLabelByCategoryId, hasNodeClass, hideNode, isBrowser, isDesktop, isEmpty, isMobile, isShippable, italianInItaly, loadAsync, loadedFromHistoryNavigation, loadedFromNavigation, loadedFromReload, noop, nullifyUndefinedProperties, onDOMContentLoaded, onReadyState, parseForm, parseItalianNumber, getEnvironmentIdFromCookieHeader as readEnvironmentIdFromHeaders, readFromLocalStorage, readFromSessionStorage, refineEnvironment, removeNodeClass, shouldReduceDataUse, showNode, slugify, stripVowelAccent, templater, twoDecimalPlacesPrice as twoDecimalPlaces, twoDecimalPlacesDigit, twoDecimalPlacesPrice, unicodeSlice, urnTransformer, wholeNumberPrice as wholeNumber, wholeNumberDigit, wholeNumberPrice };
//# sourceMappingURL=index.js.map
